import { Injectable } from '@angular/core';
import { CommodityService } from '../../data/commodity.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from '../index';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommodityFilterService } from '../../modules/shared/filter/commodity-filter.service';
import {
    commodityTableAddAssist, commodityTableChangePage,
    commodityTableChangeQuery, commodityTableDelete, commodityTableDeleteSuccess, commodityTableFailure,
    commodityTableRemoveAssist,
    commodityTableRequest, commodityTableSort,
    commodityTableSuccess
} from './commodity-table.actions';
import { catchError, concatMap, map, mergeMap, tap, toArray, withLatestFrom } from 'rxjs/operators';
import { selectCommodityTableDeleting, selectCommodityTableQuery, selectCommodityTableSelection } from './commodity-table.state';
import { from, of } from 'rxjs';

@Injectable()
export class CommodityTableEffects {
    constructor(private actions$: Actions, private commodityService: CommodityService, private store: Store<AppState>,
                private snackbar: MatSnackBar, private commodityFilterService: CommodityFilterService) {
    }

    commodityTableRequest$ = createEffect(() => this.actions$.pipe(
        ofType(commodityTableRequest),
        withLatestFrom(this.store.select(selectCommodityTableQuery)),
        mergeMap(([ , query ]) => this.commodityService.query(query).pipe(
            map((result) => commodityTableSuccess(result))
        )),
        catchError((e) => of(commodityTableFailure()))
    ));

    commodityTableFailure$ = createEffect(() => this.actions$.pipe(
        ofType(commodityTableFailure),
        tap(() => this.snackbar.open($localize`We are having difficulty communicating with the server.  Please try again later.`))
    ), { dispatch: false });

    commodityTableChangeQuery$ = createEffect(() => this.actions$.pipe(
        ofType(commodityTableChangeQuery, commodityTableChangePage, commodityTableSort),
        map(() => commodityTableRequest())
    ));

    commodityTableFilterAdd$ = createEffect(() => this.actions$.pipe(
        ofType(commodityTableAddAssist),
        withLatestFrom(this.store.select(selectCommodityTableQuery)),
        map(([ action, query ]) => this.commodityFilterService.applyAssist(query, action.id, action.parameter)),
        map(query => commodityTableChangeQuery({ query }))
    ));

    commodityTableFilterRemove$ = createEffect(() => this.actions$.pipe(
        ofType(commodityTableRemoveAssist),
        withLatestFrom(this.store.select(selectCommodityTableQuery)),
        map(([ action, query ]) => this.commodityFilterService.removeAssist(query, action.id)),
        map(query => commodityTableChangeQuery({ query }))
    ));

    commodityTableDelete$ = createEffect(() => this.actions$.pipe(
        ofType(commodityTableDelete),
        withLatestFrom(this.store.select(selectCommodityTableDeleting)),
        map(([ _, selection ]) => selection),
        concatMap(( selection ) => from(selection).pipe(
            concatMap(id => this.commodityService.delete(id)),
            toArray(),
            map(() => commodityTableDeleteSuccess()),
            catchError((e) => of(commodityTableFailure()))
        ))
    ));

    commodityTableDeleteSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(commodityTableDeleteSuccess),
        tap(() => this.snackbar.open($localize`Commodities deleted successfully.`)),
        map(() => commodityTableRequest())
    ));

    commodityTableDeleteFailure$ = createEffect(() => this.actions$.pipe(
        ofType(commodityTableFailure),
        tap(() => this.snackbar.open($localize`We are having difficulty communicating with the server.  Please try again later.`)),
        map(() => commodityTableRequest())
    ));


}
